import React from "react";
import { DateRange } from "react-day-picker";
import { format, isSameDay } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { controlSizes } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface WorkflowDateFilterProps {
  value?: DateRange;
  onChange: (range: DateRange | undefined) => void;
}

function formatLabel(range?: DateRange): string {
  if (!range?.from) return "Any date";
  if (!range.to || isSameDay(range.from, range.to)) {
    return format(range.from, "MMM d, yyyy");
  }
  return `${format(range.from, "MMM d")} – ${format(range.to, "MMM d, yyyy")}`;
}

const WorkflowDateFilter: React.FC<WorkflowDateFilterProps> = ({ value, onChange }) => {
  const hasValue = Boolean(value?.from);

  return (
    <div className="flex w-full flex-col gap-1 sm:w-[180px]">
      <span className="text-xs font-medium text-muted-foreground">Created</span>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            data-testid="workflow-date-filter"
            className={cn(
              controlSizes.default,
              "w-full justify-start bg-background font-normal",
              !hasValue && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
            <span className="truncate">{formatLabel(value)}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            selected={value}
            onSelect={onChange}
            numberOfMonths={1}
            defaultMonth={value?.from}
          />
          <div className="flex justify-end border-t p-2">
            <Button
              variant="ghost"
              size="sm"
              data-testid="workflow-date-filter-clear"
              disabled={!hasValue}
              onClick={() => onChange(undefined)}
            >
              <X className="mr-1 h-3.5 w-3.5" />
              Clear
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default WorkflowDateFilter;
